import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Phone, ChevronRight, CheckCircle, XCircle, CreditCard, Wifi, AlertTriangle, RefreshCw } from "lucide-react";
import axios from "axios";
import { KioskViewport } from "../../components/ui/kiosk-viewport";
import { KioskHeader } from "../../components/ui/kiosk-header";
import { toast } from "sonner";
import { getStoredKioskId } from "./KioskSetup";

const API = '/api';
const IDLE_TIMEOUT = 60;
const POLL_INTERVAL = 2000;
const TERMINAL_TIMEOUT = 90000; // 90 seconds terminal timeout

// Payment state machine
const PAYMENT_STATES = {
  idle: 'idle',
  connecting: 'connecting',      // Connecting to Stripe Terminal
  terminal_ready: 'terminal_ready', // Terminal ready, waiting for card
  waiting_card: 'waiting_card',   // Actively waiting for card tap/insert
  processing: 'processing',       // Card detected, processing payment
  approved: 'approved',           // Payment successful
  failed: 'failed',               // Payment failed
  timeout: 'timeout'              // Terminal didn't respond
};

// Terminal Direction Indicator Component
function TerminalDirectionIndicator({ position = 'right' }) {
  // Position can be: 'right', 'left', 'below'
  const arrowStyle = {
    right: { transform: 'rotate(0deg)', marginLeft: '20px' },
    left: { transform: 'rotate(180deg)', marginRight: '20px' },
    below: { transform: 'rotate(90deg)', marginTop: '20px' }
  };
  
  return (
    <div 
      data-testid="terminal-direction-indicator"
      style={{
        display: 'flex',
        flexDirection: position === 'below' ? 'column' : 'row',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'pulse-glow 2s ease-in-out infinite',
        padding: '16px'
      }}
    >
      {/* Animated Arrow */}
      <div style={{
        ...arrowStyle[position],
        animation: `arrow-bounce-${position} 1s ease-in-out infinite`
      }}>
        <svg width="80" height="60" viewBox="0 0 80 60" fill="none">
          <defs>
            <linearGradient id="arrowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#22c55e" />
              <stop offset="100%" stopColor="#16a34a" />
            </linearGradient>
          </defs>
          <path 
            d="M0 25 L50 25 L50 10 L80 30 L50 50 L50 35 L0 35 Z" 
            fill="url(#arrowGradient)"
            style={{ filter: 'drop-shadow(0 4px 8px rgba(34, 197, 94, 0.4))' }}
          />
        </svg>
      </div>
      
      {/* Terminal Icon */}
      <div style={{
        background: 'linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)',
        borderRadius: '16px',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)',
        border: '3px solid #22c55e',
        animation: 'terminal-glow 2s ease-in-out infinite'
      }}>
        <CreditCard size={48} color="#22c55e" />
        <span style={{ color: '#22c55e', fontSize: '14px', fontWeight: '700', letterSpacing: '1px' }}>
          TERMINAL
        </span>
      </div>
      
      <style>{`
        @keyframes arrow-bounce-right {
          0%, 100% { transform: translateX(0) rotate(0deg); }
          50% { transform: translateX(15px) rotate(0deg); }
        }
        @keyframes arrow-bounce-left {
          0%, 100% { transform: translateX(0) rotate(180deg); }
          50% { transform: translateX(-15px) rotate(180deg); }
        }
        @keyframes arrow-bounce-below {
          0%, 100% { transform: translateY(0) rotate(90deg); }
          50% { transform: translateY(15px) rotate(90deg); }
        }
        @keyframes pulse-glow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.85; }
        }
        @keyframes terminal-glow {
          0%, 100% { box-shadow: 0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1), 0 0 20px rgba(34, 197, 94, 0.3); }
          50% { box-shadow: 0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1), 0 0 40px rgba(34, 197, 94, 0.6); }
        }
      `}</style>
    </div>
  );
}

// Payment State Display Component
function PaymentStateDisplay({ state, error, onRetry, formatCurrency, amount }) {
  const stateConfig = {
    [PAYMENT_STATES.connecting]: {
      icon: <Wifi size={64} color="#3b82f6" style={{ animation: 'pulse 1.5s ease-in-out infinite' }} />,
      title: 'Connecting to Terminal',
      subtitle: 'Please wait...',
      color: '#3b82f6',
      showTerminal: false
    },
    [PAYMENT_STATES.terminal_ready]: {
      icon: <CreditCard size={64} color="#22c55e" />,
      title: 'Terminal Ready',
      subtitle: 'Please tap or insert your card now',
      color: '#22c55e',
      showTerminal: true
    },
    [PAYMENT_STATES.waiting_card]: {
      icon: <CreditCard size={64} color="#22c55e" style={{ animation: 'pulse 1s ease-in-out infinite' }} />,
      title: 'Tap Your Card Now',
      subtitle: 'Hold your card against the terminal',
      color: '#22c55e',
      showTerminal: true
    },
    [PAYMENT_STATES.processing]: {
      icon: <Loader2 size={64} color="#f59e0b" style={{ animation: 'spin 1s linear infinite' }} />,
      title: 'Processing Payment',
      subtitle: `Charging ${formatCurrency(amount)}...`,
      color: '#f59e0b',
      showTerminal: false
    },
    [PAYMENT_STATES.approved]: {
      icon: <CheckCircle size={80} color="#22c55e" />,
      title: 'Payment Approved!',
      subtitle: 'You may now exit the car park',
      color: '#22c55e',
      showTerminal: false
    },
    [PAYMENT_STATES.failed]: {
      icon: <XCircle size={64} color="#ef4444" />,
      title: 'Payment Failed',
      subtitle: error || 'Please try again',
      color: '#ef4444',
      showTerminal: false,
      showRetry: true
    },
    [PAYMENT_STATES.timeout]: {
      icon: <AlertTriangle size={64} color="#f59e0b" />,
      title: 'Terminal Not Responding',
      subtitle: 'The terminal did not respond in time',
      color: '#f59e0b',
      showTerminal: false,
      showRetry: true
    }
  };
  
  const config = stateConfig[state];
  if (!config) return null;
  
  return (
    <div 
      data-testid={`payment-state-${state}`}
      style={{
        background: 'white',
        borderRadius: '24px',
        padding: '40px',
        width: '100%',
        maxWidth: '700px',
        boxShadow: '0 12px 48px rgba(0,0,0,0.15)',
        border: `4px solid ${config.color}`,
        textAlign: 'center'
      }}
    >
      <div style={{ marginBottom: '24px' }}>
        {config.icon}
      </div>
      
      <h3 style={{ 
        fontSize: '36px', 
        fontWeight: '800', 
        color: config.color, 
        marginBottom: '12px',
        textTransform: 'uppercase',
        letterSpacing: '1px'
      }}>
        {config.title}
      </h3>
      
      <p style={{ 
        fontSize: '22px', 
        color: '#64748b', 
        marginBottom: config.showTerminal ? '32px' : '24px',
        lineHeight: 1.4
      }}>
        {config.subtitle}
      </p>
      
      {config.showTerminal && (
        <div style={{
          background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
          borderRadius: '20px',
          padding: '32px',
          marginBottom: '24px'
        }}>
          <p style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#166534',
            marginBottom: '20px'
          }}>
            ➡️ TAP OR INSERT CARD ON THE PAYMENT TERMINAL TO YOUR RIGHT
          </p>
          <TerminalDirectionIndicator position="right" />
        </div>
      )}
      
      {config.showRetry && (
        <button
          onClick={onRetry}
          data-testid="payment-retry-button"
          style={{
            padding: '18px 48px',
            borderRadius: '14px',
            border: 'none',
            cursor: 'pointer',
            fontSize: '22px',
            fontWeight: '700',
            color: 'white',
            background: '#3b82f6',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '12px',
            marginTop: '16px'
          }}
        >
          <RefreshCw size={24} /> Try Again
        </button>
      )}
      
      {state === PAYMENT_STATES.approved && (
        <div style={{ marginTop: '24px' }}>
          <p style={{ fontSize: '20px', color: '#16a34a', fontWeight: '600', marginBottom: '16px' }}>
            ✓ Exit barrier will open automatically
          </p>
        </div>
      )}
      
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(0.95); }
        }
      `}</style>
    </div>
  );
}

function VirtualKeyboard({ value, onChange, onSubmit }) {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });

  const keys = [
    ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
    ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
    ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
    ['Z', 'X', 'C', 'V', 'B', 'N', 'M', 'DEL']
  ];

  const handleKey = (key) => {
    if (key === 'DEL') {
      onChange(value.slice(0, -1));
    } else if (value.length < 9) {
      onChange(value + key);
    }
  };

  const handleDragStart = (e) => {
    if (e.target.closest('[data-drag-handle]')) {
      setIsDragging(true);
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      dragStartRef.current = { x: clientX - position.x, y: clientY - position.y };
      e.preventDefault();
    }
  };

  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setPosition({
      x: clientX - dragStartRef.current.x,
      y: clientY - dragStartRef.current.y
    });
  }, [isDragging]);

  const handleDragEnd = useCallback(() => setIsDragging(false), []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove, { passive: false });
      window.addEventListener('touchend', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
        window.removeEventListener('touchmove', handleDragMove);
        window.removeEventListener('touchend', handleDragEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);

  return (
    <div 
      style={{ 
        transform: `translate(${position.x}px, ${position.y}px)`,
        touchAction: 'none',
        background: '#1e293b', 
        borderRadius: '24px', 
        padding: '20px 24px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
      }}
      onMouseDown={handleDragStart}
      onTouchStart={handleDragStart}
    >
      <div 
        data-drag-handle
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          marginBottom: '16px',
          padding: '12px',
          cursor: 'grab',
          background: '#334155',
          borderRadius: '12px'
        }}
      >
        <span style={{ color: '#94a3b8', fontSize: '18px', fontWeight: '500' }}>☰ Drag to move keyboard</span>
      </div>
      {keys.map((row, rowIndex) => (
        <div key={rowIndex} style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
          {row.map((key) => (
            <button
              key={key}
              onClick={() => handleKey(key)}
              style={{
                padding: '20px 24px',
                borderRadius: '12px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '24px',
                fontWeight: '700',
                color: 'white',
                background: key === 'DEL' ? '#ef4444' : '#475569',
                minWidth: '60px'
              }}
              data-testid={`key-${key}`}
            >
              {key === 'DEL' ? '⌫' : key}
            </button>
          ))}
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '12px' }}>
        <button
          onClick={onSubmit}
          disabled={value.length < 5}
          style={{
            padding: '20px 80px',
            borderRadius: '12px',
            border: 'none',
            cursor: value.length >= 5 ? 'pointer' : 'not-allowed',
            fontSize: '24px',
            fontWeight: '700',
            color: 'white',
            background: value.length >= 5 ? '#22c55e' : '#64748b'
          }}
          data-testid="key-check-fee"
        >
          CHECK FEE
        </button>
      </div>
    </div>
  );
}

export default function KioskPayKiosk() {
  const navigate = useNavigate();
  
  // Get persistent kiosk identity from localStorage
  const KIOSK_ID = getStoredKioskId();
  
  const [vehicleReg, setVehicleReg] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const [error, setError] = useState(null);
  const [paymentState, setPaymentState] = useState(PAYMENT_STATES.idle);
  const idleTimerRef = useRef(null);
  const pollRef = useRef(null);
  
  // FREE EXIT AUTHORISATION STATE
  const [freeExitLoading, setFreeExitLoading] = useState(false);
  const [freeExitError, setFreeExitError] = useState(null);
  const [freeExitAuthorised, setFreeExitAuthorised] = useState(false);
  
  // NO SESSION SAFETY STATE - allows exit without session
  const [noSessionSafety, setNoSessionSafety] = useState(false);
  
  // O/0 Confirmation Dialog State (P0 Kiosk Fix)
  const [showO0Confirm, setShowO0Confirm] = useState(false);
  const [pendingO0Match, setPendingO0Match] = useState(null);
  
  // Redirect to setup if kiosk not configured
  useEffect(() => {
    if (!KIOSK_ID) {
      navigate("/kiosk/setup");
    }
  }, [KIOSK_ID, navigate]);

  useEffect(() => {
    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => navigate('/kiosk/attract'), IDLE_TIMEOUT * 1000);
    };
    const events = ['mousedown', 'touchstart', 'keydown', 'input'];
    events.forEach(e => document.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach(e => document.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [navigate]);
  
  // Don't render if not configured
  if (!KIOSK_ID) {
    return null;
  }

  const handleCheckTariff = async () => {
    if (!vehicleReg.trim() || vehicleReg.length < 5) {
      setError("Please enter a valid vehicle registration");
      return;
    }
    setLoading(true);
    setError(null);
    setShowO0Confirm(false);
    setPendingO0Match(null);
    
    const vrm = vehicleReg.trim().toUpperCase();
    
    try {
      // STEP 1: Try exact match first
      const response = await axios.get(`${API}/parking/session/${encodeURIComponent(vrm)}/calculate`);
      
      if (response.data && response.data.session_id) {
        setSessionData({
          session_id: response.data.session_id,
          vehicle_registration: response.data.vehicle_registration,
          duration_display: response.data.duration_display,
          amount_due: response.data.amount_due,
          is_grace_period: response.data.is_grace_period,
          has_entitlement: response.data.has_entitlement,
          entitlement_discount: response.data.entitlement_discount,
          is_whitelisted: response.data.is_whitelisted ?? false,
          whitelist_reason: response.data.whitelist_reason ?? null
        });
      } else {
        setError("Unexpected response from server.");
      }
    } catch (err) {
      if (err.response?.status === 404) {
        // STEP 2: No exact match - try O/0 fuzzy matching
        try {
          const fuzzyResponse = await axios.get(
            `${API}/parking/session/${encodeURIComponent(vrm)}/calculate?fuzzy=o0`
          );
          
          if (fuzzyResponse.data?.match_type === "o0_alternate") {
            // Single O/0 alternate found - show confirmation dialog
            setPendingO0Match({
              originalVrm: fuzzyResponse.data.original_vrm,
              matchedVrm: fuzzyResponse.data.matched_vrm,
              sessionData: {
                session_id: fuzzyResponse.data.session_id,
                vehicle_registration: fuzzyResponse.data.vehicle_registration,
                duration_display: fuzzyResponse.data.duration_display,
                amount_due: fuzzyResponse.data.amount_due,
                is_grace_period: fuzzyResponse.data.is_grace_period,
                has_entitlement: fuzzyResponse.data.has_entitlement,
                entitlement_discount: fuzzyResponse.data.entitlement_discount,
                is_whitelisted: fuzzyResponse.data.is_whitelisted ?? false,
                whitelist_reason: fuzzyResponse.data.whitelist_reason ?? null
              }
            });
            setShowO0Confirm(true);
          } else if (fuzzyResponse.data?.session_id) {
            // Exact match found on fuzzy call (shouldn't happen, but handle gracefully)
            setSessionData({
              session_id: fuzzyResponse.data.session_id,
              vehicle_registration: fuzzyResponse.data.vehicle_registration,
              duration_display: fuzzyResponse.data.duration_display,
              amount_due: fuzzyResponse.data.amount_due,
              is_grace_period: fuzzyResponse.data.is_grace_period,
              has_entitlement: fuzzyResponse.data.has_entitlement,
              entitlement_discount: fuzzyResponse.data.entitlement_discount,
              is_whitelisted: fuzzyResponse.data.is_whitelisted ?? false,
              whitelist_reason: fuzzyResponse.data.whitelist_reason ?? null
            });
          }
        } catch (fuzzyErr) {
          // Check for MULTIPLE_POSSIBLE_MATCHES from O/0
          if (fuzzyErr.response?.data?.detail?.error === "MULTIPLE_POSSIBLE_MATCHES") {
            setError("We found more than one possible registration. Please check your registration and try again.");
          } else {
            // STEP 3: No O/0 match - try single-character typo-assist (site_001 only)
            try {
              const typoResponse = await axios.get(
                `${API}/parking/session/${encodeURIComponent(vrm)}/calculate?fuzzy=typo&site_id=site_001`
              );
              
              if (typoResponse.data?.match_type === "typo_assist") {
                // Single typo match found - show confirmation dialog
                setPendingO0Match({
                  originalVrm: typoResponse.data.original_vrm,
                  matchedVrm: typoResponse.data.matched_vrm,
                  sessionData: {
                    session_id: typoResponse.data.session_id,
                    vehicle_registration: typoResponse.data.vehicle_registration,
                    duration_display: typoResponse.data.duration_display,
                    amount_due: typoResponse.data.amount_due,
                    is_grace_period: typoResponse.data.is_grace_period,
                    has_entitlement: typoResponse.data.has_entitlement,
                    entitlement_discount: typoResponse.data.entitlement_discount,
                    is_whitelisted: typoResponse.data.is_whitelisted ?? false,
                    whitelist_reason: typoResponse.data.whitelist_reason ?? null
                  }
                });
                setShowO0Confirm(true);
              } else if (typoResponse.data?.session_id) {
                // Exact match found (shouldn't happen, but handle gracefully)
                setSessionData({
                  session_id: typoResponse.data.session_id,
                  vehicle_registration: typoResponse.data.vehicle_registration,
                  duration_display: typoResponse.data.duration_display,
                  amount_due: typoResponse.data.amount_due,
                  is_grace_period: typoResponse.data.is_grace_period,
                  has_entitlement: typoResponse.data.has_entitlement,
                  entitlement_discount: typoResponse.data.entitlement_discount,
                  is_whitelisted: typoResponse.data.is_whitelisted ?? false,
                  whitelist_reason: typoResponse.data.whitelist_reason ?? null
                });
              }
            } catch (typoErr) {
              // Check for MULTIPLE_POSSIBLE_MATCHES from typo-assist
              if (typoErr.response?.data?.detail?.error === "MULTIPLE_POSSIBLE_MATCHES") {
                setError("We found more than one possible registration. Please check your registration and try again.");
              } else {
                // No match found at all - PROTECTED ParkWise SAFETY BEHAVIOUR
                // Vehicle must not be trapped - allow free exit without session
                setNoSessionSafety(true);
                setError(null);
              }
            }
          }
        }
      } else {
        // API/network error
        setError(err.response?.data?.detail || "Unable to check fee. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Handle O/0 confirmation dialog responses
  const handleO0Confirm = (confirmed) => {
    if (confirmed && pendingO0Match) {
      // Customer confirmed - use the matched VRM and session
      setVehicleReg(pendingO0Match.matchedVrm);
      setSessionData(pendingO0Match.sessionData);
    } else {
      // Customer rejected - clear and return to VRM entry
      setVehicleReg("");
    }
    setShowO0Confirm(false);
    setPendingO0Match(null);
  };

  // P0 FIX: Handle free exit - must call authorize-free-exit endpoint before confirmation
  const handleFreeExit = async () => {
    if (!sessionData?.session_id || !sessionData?.vehicle_registration) return;
    
    setFreeExitLoading(true);
    setFreeExitError(null);
    
    try {
      // Call authorize-free-exit endpoint with FreeExitRequest schema
      const response = await axios.post(
        `${API}/parking/session/${encodeURIComponent(sessionData.vehicle_registration)}/authorize-free-exit`,
        {
          session_id: sessionData.session_id,
          apply_entitlement: true
        }
      );
      
      if (response.data?.success) {
        // Exit authorisation created - now show confirmation
        setFreeExitAuthorised(true);
        setPaymentState(PAYMENT_STATES.approved);
        toast.success("Free parking confirmed - you may now exit!");
      } else {
        // Unexpected response
        setFreeExitError("Unable to authorise exit. Please try again.");
      }
    } catch (err) {
      // API failure - do NOT show success, allow retry
      const errorMsg = err.response?.data?.detail || "Unable to authorise free exit. Please try again.";
      setFreeExitError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setFreeExitLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!sessionData?.session_id) return;
    
    // P0 FIX: FREE EXIT must call authorize-free-exit endpoint before showing confirmation
    if (sessionData.is_grace_period || sessionData.amount_due <= 0) {
      await handleFreeExit();
      return;
    }
    
    // STEP 1: Show connecting state IMMEDIATELY
    setPaymentState(PAYMENT_STATES.connecting);
    setError(null);
    
    try {
      // STEP 2: Create PaymentIntent and initiate terminal collection
      // Backend will look up reader assigned to this kiosk
      const response = await axios.post(`${API}/terminal/intent`, {
        session_id: sessionData.session_id,
        kiosk_id: KIOSK_ID
      });
      
      if (response.data.payment_intent_id) {
        const paymentIntentId = response.data.payment_intent_id;
        const terminalStatus = response.data.status;
        
        // STEP 3: Show terminal ready / waiting for card
        if (terminalStatus === 'processing') {
          setPaymentState(PAYMENT_STATES.waiting_card);
        } else if (terminalStatus === 'reader_error') {
          setPaymentState(PAYMENT_STATES.failed);
          setError("Terminal offline. Please try again or call for assistance.");
          return;
        } else {
          // requires_reader or other
          setPaymentState(PAYMENT_STATES.terminal_ready);
        }
        
        // STEP 4: Poll for payment completion
        let pollCount = 0;
        const maxPolls = Math.floor(TERMINAL_TIMEOUT / POLL_INTERVAL);
        
        pollRef.current = setInterval(async () => {
          pollCount++;
          
          try {
            const statusRes = await axios.get(`${API}/terminal/status/${paymentIntentId}`);
            const stripeStatus = statusRes.data.status;
            
            // Update UI based on Stripe status
            if (stripeStatus === 'requires_payment_method') {
              setPaymentState(PAYMENT_STATES.waiting_card);
            } else if (stripeStatus === 'processing') {
              setPaymentState(PAYMENT_STATES.processing);
            } else if (stripeStatus === 'succeeded') {
              clearInterval(pollRef.current);
              
              // Check for exit authorisation
              if (statusRes.data.has_exit_authorisation) {
                setPaymentState(PAYMENT_STATES.approved);
                toast.success("Payment successful! You may now exit.");
              } else {
                // Payment succeeded but exit auth pending (webhook delay)
                setPaymentState(PAYMENT_STATES.processing);
                // Continue polling briefly for exit auth
                setTimeout(async () => {
                  const finalCheck = await axios.get(`${API}/terminal/status/${paymentIntentId}`);
                  if (finalCheck.data.has_exit_authorisation) {
                    setPaymentState(PAYMENT_STATES.approved);
                  } else {
                    setPaymentState(PAYMENT_STATES.approved);
                    toast.info("Payment confirmed. Exit gate will open.");
                  }
                }, 3000);
              }
            } else if (stripeStatus === 'canceled' || stripeStatus === 'requires_action') {
              clearInterval(pollRef.current);
              setPaymentState(PAYMENT_STATES.failed);
              setError("Payment was cancelled. Please try again.");
            }
            
            // Timeout check
            if (pollCount >= maxPolls) {
              clearInterval(pollRef.current);
              setPaymentState(PAYMENT_STATES.timeout);
              setError("Terminal timed out. Please try again.");
            }
            
          } catch (e) {
            console.error("Poll error:", e);
            // Don't fail on single poll error, continue polling
          }
        }, POLL_INTERVAL);
        
      } else {
        setPaymentState(PAYMENT_STATES.failed);
        setError(response.data.detail || "Failed to connect to terminal.");
      }
    } catch (err) {
      setPaymentState(PAYMENT_STATES.failed);
      setError(err.response?.data?.detail || "Failed to initiate payment. Please try again.");
    }
  };
  
  const handleRetry = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setPaymentState(PAYMENT_STATES.idle);
    setError(null);
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '£0.00';
    return `£${Number(amount).toFixed(2)}`;
  };

  return (
    <KioskViewport>
      <div 
        data-testid="kiosk-pay-kiosk"
        style={{ 
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
          overflow: 'hidden',
          background: '#ffffff'
        }}
      >
        <KioskHeader showDateTime={true} />

        <div style={{ padding: '20px 40px', background: 'white' }}>
          <button 
            onClick={() => navigate("/kiosk")}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '14px 24px',
              borderRadius: '14px',
              background: '#1e3a5f',
              border: 'none',
              cursor: 'pointer'
            }}
            data-testid="back-button"
          >
            <ArrowLeft size={28} color="white" />
            <span style={{ color: 'white', fontSize: '20px', fontWeight: '700' }}>Back</span>
          </button>
        </div>

        <div style={{
          position: 'relative',
          height: '480px',
          overflow: 'hidden',
          flexShrink: 0
        }}>
          <img 
            src="/cityscape.png" 
            alt="Ayr Town Centre"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center bottom'
            }}
          />
          <img 
            src="/tracy.png" 
            alt="Tracy - ParkWise Assistant"
            style={{
              position: 'absolute',
              right: '-20px',
              bottom: '-40px',
              height: '560px',
              zIndex: 10
            }}
          />
          <div style={{
            position: 'absolute',
            top: '30px',
            left: '40px',
            width: '480px',
            background: '#fef9e7',
            borderRadius: '24px',
            padding: '28px 32px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            zIndex: 20
          }}>
            <h3 style={{ fontSize: '32px', fontWeight: '700', color: '#1e3a5f', margin: '0 0 16px 0' }}>
              Ready to leave? Enter your registration below.
            </h3>
            <p style={{ fontSize: '20px', color: '#374151', lineHeight: 1.5, margin: 0 }}>
              Enter your vehicle registration to check your parking fee.
            </p>
            <div style={{
              position: 'absolute',
              right: '-22px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: 0,
              height: 0,
              borderTop: '18px solid transparent',
              borderBottom: '18px solid transparent',
              borderLeft: '24px solid #fef9e7'
            }} />
          </div>
        </div>

        <div style={{
          flex: 1,
          padding: '24px 40px',
          background: 'linear-gradient(180deg, #f8fafc 0%, #e0f2fe 100%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px'
        }}>
          <div style={{
            width: '600px',
            background: '#fef08a',
            borderRadius: '16px',
            padding: '24px 32px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            border: '4px solid #eab308'
          }}>
            <div style={{
              background: '#1e40af',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '8px',
              fontSize: '24px',
              fontWeight: '700'
            }}>GB</div>
            <span style={{
              flex: 1,
              fontSize: '48px',
              fontWeight: '700',
              color: '#1e293b',
              letterSpacing: '8px',
              textAlign: 'center',
              fontFamily: 'monospace'
            }}>
              {vehicleReg || 'ENTER REG'}
            </span>
          </div>

          {error && (
            <div style={{
              background: '#fef2f2',
              border: '2px solid #ef4444',
              borderRadius: '12px',
              padding: '16px 24px',
              color: '#dc2626',
              fontSize: '18px',
              fontWeight: '600'
            }}>
              {error}
            </div>
          )}

          {/* O/0 Confirmation Dialog (P0 Kiosk Fix) */}
          {showO0Confirm && pendingO0Match && (
            <div 
              data-testid="vrm-confirmation-dialog"
              style={{
                background: 'white',
                borderRadius: '24px',
                padding: '40px',
                width: '100%',
                maxWidth: '650px',
                boxShadow: '0 12px 48px rgba(0,0,0,0.2)',
                border: '4px solid #3b82f6',
                textAlign: 'center'
              }}
            >
              <h3 style={{ 
                fontSize: '32px', 
                fontWeight: '700', 
                color: '#1e3a5f', 
                marginBottom: '24px' 
              }}>
                We found this registration:
              </h3>
              
              {/* Matched VRM Display */}
              <div style={{
                background: '#fef08a',
                borderRadius: '16px',
                padding: '20px 32px',
                marginBottom: '20px',
                border: '4px solid #eab308',
                display: 'inline-block'
              }}>
                <span style={{
                  fontSize: '52px',
                  fontWeight: '700',
                  color: '#1e293b',
                  letterSpacing: '6px',
                  fontFamily: 'monospace'
                }}>
                  {pendingO0Match.matchedVrm}
                </span>
              </div>
              
              <p style={{ 
                fontSize: '22px', 
                color: '#64748b', 
                marginBottom: '32px' 
              }}>
                You entered: <strong style={{ color: '#1e3a5f' }}>{pendingO0Match.originalVrm}</strong>
              </p>
              
              <h4 style={{ 
                fontSize: '28px', 
                fontWeight: '700', 
                color: '#1e3a5f', 
                marginBottom: '24px' 
              }}>
                Is this your vehicle?
              </h4>
              
              <div style={{ 
                display: 'flex', 
                gap: '20px', 
                justifyContent: 'center' 
              }}>
                <button
                  onClick={() => handleO0Confirm(true)}
                  data-testid="confirm-vrm-yes"
                  style={{
                    padding: '20px 40px',
                    borderRadius: '14px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '24px',
                    fontWeight: '700',
                    color: 'white',
                    background: '#22c55e',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}
                >
                  <CheckCircle size={28} /> YES, THIS IS MY VEHICLE
                </button>
                
                <button
                  onClick={() => handleO0Confirm(false)}
                  data-testid="confirm-vrm-no"
                  style={{
                    padding: '20px 40px',
                    borderRadius: '14px',
                    border: '3px solid #dc2626',
                    cursor: 'pointer',
                    fontSize: '24px',
                    fontWeight: '700',
                    color: '#dc2626',
                    background: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}
                >
                  <XCircle size={28} /> NO, TRY AGAIN
                </button>
              </div>
            </div>
          )}

          {paymentState === PAYMENT_STATES.approved ? (
            <PaymentStateDisplay 
              state={PAYMENT_STATES.approved}
              formatCurrency={formatCurrency}
              amount={sessionData?.amount_due}
            />
          ) : paymentState === PAYMENT_STATES.connecting || 
              paymentState === PAYMENT_STATES.terminal_ready || 
              paymentState === PAYMENT_STATES.waiting_card || 
              paymentState === PAYMENT_STATES.processing ? (
            <PaymentStateDisplay 
              state={paymentState}
              error={error}
              onRetry={handleRetry}
              formatCurrency={formatCurrency}
              amount={sessionData?.amount_due}
            />
          ) : paymentState === PAYMENT_STATES.failed || paymentState === PAYMENT_STATES.timeout ? (
            <PaymentStateDisplay 
              state={paymentState}
              error={error}
              onRetry={handleRetry}
              formatCurrency={formatCurrency}
              amount={sessionData?.amount_due}
            />
          ) : sessionData ? (
            <div style={{
              background: 'white',
              borderRadius: '20px',
              padding: '32px',
              width: '100%',
              maxWidth: '600px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              border: '3px solid #22c55e'
            }}>
              <h3 style={{ fontSize: '28px', fontWeight: '700', color: '#1e3a5f', marginBottom: '16px' }}>
                Parking Fee
              </h3>
              {sessionData.is_whitelisted ? (
                <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '20px', color: '#64748b' }}>Parking Status:</span>
                    <span style={{ fontSize: '20px', fontWeight: '600', color: '#22c55e' }}>No payment required</span>
                  </div>
                  <p style={{ fontSize: '14px', color: '#94a3b8', marginTop: '8px' }}>
                    This vehicle is registered for free parking at this location.
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <span style={{ fontSize: '20px', color: '#64748b' }}>Duration:</span>
                  <span style={{ fontSize: '20px', fontWeight: '600', color: '#1e3a5f' }}>{sessionData.duration_display}</span>
                </div>
              )}
              {sessionData.has_entitlement && sessionData.entitlement_discount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <span style={{ fontSize: '20px', color: '#64748b' }}>Retailer Discount:</span>
                  <span style={{ fontSize: '20px', fontWeight: '600', color: '#22c55e' }}>-{formatCurrency(sessionData.entitlement_discount)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
                <span style={{ fontSize: '20px', color: '#64748b' }}>Amount Due:</span>
                <span style={{ fontSize: '32px', fontWeight: '700', color: '#16a34a' }}>{formatCurrency(sessionData.amount_due)}</span>
              </div>
              {sessionData.is_grace_period || sessionData.amount_due <= 0 ? (
                <div style={{ textAlign: 'center' }}>
                  {freeExitAuthorised ? (
                    // P0 FIX: Show confirmation ONLY after authorize-free-exit succeeds
                    <>
                      <p style={{ fontSize: '24px', color: '#22c55e', fontWeight: '700', marginBottom: '16px' }}>
                        ✓ FREE PARKING CONFIRMED
                      </p>
                      <p style={{ fontSize: '18px', color: '#16a34a', marginBottom: '24px' }}>
                        Exit authorised - you may now leave the car park
                      </p>
                      <button
                        onClick={() => navigate("/kiosk/attract")}
                        data-testid="free-exit-done-button"
                        style={{
                          width: '100%',
                          padding: '20px',
                          borderRadius: '12px',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '24px',
                          fontWeight: '700',
                          color: 'white',
                          background: '#22c55e',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '12px'
                        }}
                      >
                        <CheckCircle size={28} /> DONE
                      </button>
                    </>
                  ) : freeExitLoading ? (
                    // Loading state while authorising
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                      <Loader2 size={48} color="#16a34a" style={{ animation: 'spin 1s linear infinite' }} />
                      <p style={{ fontSize: '20px', color: '#1e3a5f', fontWeight: '600' }}>
                        Authorising free exit...
                      </p>
                    </div>
                  ) : freeExitError ? (
                    // Error state with retry
                    <div>
                      <p style={{ fontSize: '18px', color: '#dc2626', fontWeight: '600', marginBottom: '16px' }}>
                        {freeExitError}
                      </p>
                      <button
                        onClick={handleFreeExit}
                        data-testid="free-exit-retry-button"
                        style={{
                          width: '100%',
                          padding: '20px',
                          borderRadius: '12px',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '24px',
                          fontWeight: '700',
                          color: 'white',
                          background: '#3b82f6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '12px'
                        }}
                      >
                        <RefreshCw size={28} /> TRY AGAIN
                      </button>
                    </div>
                  ) : (
                    // Initial free exit state - prompt user to authorise
                    <>
                      <p style={{ fontSize: '20px', color: '#22c55e', fontWeight: '600', marginBottom: '16px' }}>
                        {sessionData.is_whitelisted 
                          ? "No payment required - registered for free parking"
                          : "No payment required - you are within the free period!"}
                      </p>
                      <button
                        onClick={handleFreeExit}
                        data-testid="free-exit-button"
                        style={{
                          width: '100%',
                          padding: '20px',
                          borderRadius: '12px',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '24px',
                          fontWeight: '700',
                          color: 'white',
                          background: '#22c55e',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '12px'
                        }}
                      >
                        <CheckCircle size={28} /> EXIT FREE
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <button
                  onClick={handlePayment}
                  style={{
                    width: '100%',
                    padding: '20px',
                    borderRadius: '12px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '24px',
                    fontWeight: '700',
                    color: 'white',
                    background: '#22c55e',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px'
                  }}
                  data-testid="pay-before-exit-button"
                >
                  PAY BEFORE EXIT {formatCurrency(sessionData.amount_due)} <ChevronRight size={28} />
                </button>
              )}
            </div>
          ) : loading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Loader2 size={48} color="#16a34a" style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ fontSize: '24px', color: '#1e3a5f' }}>Checking fee...</span>
            </div>
          ) : noSessionSafety ? (
            // P0 FIX: NO SESSION SAFETY - Vehicle must not be trapped
            // Allow free exit without session - physical barrier governed by ANPR NO_SESSION_FREE_EXIT
            <div 
              data-testid="no-session-safety-panel"
              style={{
                background: 'white',
                borderRadius: '20px',
                padding: '40px',
                width: '100%',
                maxWidth: '600px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                border: '3px solid #22c55e',
                textAlign: 'center'
              }}
            >
              <CheckCircle size={64} color="#22c55e" style={{ marginBottom: '20px' }} />
              <h3 style={{ fontSize: '32px', fontWeight: '700', color: '#1e3a5f', marginBottom: '16px' }}>
                FREE PARKING
              </h3>
              <p style={{ fontSize: '20px', color: '#64748b', marginBottom: '24px' }}>
                No active session found for this vehicle.
              </p>
              <p style={{ fontSize: '18px', color: '#22c55e', fontWeight: '600', marginBottom: '32px' }}>
                You may proceed to the exit - the barrier will open automatically.
              </p>
              <button
                onClick={() => navigate("/kiosk/attract")}
                data-testid="no-session-proceed-button"
                style={{
                  width: '100%',
                  padding: '20px',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '24px',
                  fontWeight: '700',
                  color: 'white',
                  background: '#22c55e',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px'
                }}
              >
                <CheckCircle size={28} /> PROCEED TO EXIT
              </button>
            </div>
          ) : (
            <VirtualKeyboard
              value={vehicleReg}
              onChange={(val) => setVehicleReg(val.toUpperCase())}
              onSubmit={handleCheckTariff}
            />
          )}
        </div>

        <div style={{
          margin: '24px 40px',
          padding: '20px 32px',
          background: '#1e3a5f',
          borderRadius: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px'
        }}>
          <Phone size={24} color="#fbbf24" />
          <span style={{ color: 'white', fontSize: '18px', fontWeight: '600' }}>Need help? Call </span>
          <span style={{ color: '#fbbf24', fontSize: '22px', fontWeight: '700' }}>01292 833107</span>
        </div>
      </div>
    </KioskViewport>
  );
}
